import { create } from 'zustand';
import { ErrorCodes } from '@clinikchat/shared';

/**
 * Min-client-version gate, web side. Every API request carries
 * `X-ClinikChat-Client: web` and `X-ClinikChat-Client-Version`; when the server
 * answers 426 CLIENT_UPDATE_REQUIRED the app swaps to a blocking
 * "Update required" screen (components/UpdateRequiredScreen.tsx).
 */

/**
 * `<package.json version>[+<git sha>]`, injected by vite.config.ts. The server
 * compares only the semver part (build metadata is ignored), so raising
 * MIN_CLIENT_VERSION_WEB requires bumping packages/web/package.json first.
 * Falls back to a non-semver marker (never gated) when not injected, e.g. in
 * unit tests.
 */
export const CLIENT_VERSION: string =
  typeof __APP_VERSION__ === 'string' && __APP_VERSION__ ? __APP_VERSION__ : 'dev';

export const CLIENT_HEADERS = {
  'X-ClinikChat-Client': 'web',
  'X-ClinikChat-Client-Version': CLIENT_VERSION,
} as const;

interface UpdateRequiredState {
  required: boolean;
  minVersion: string | null;
  setRequired: (minVersion: string | null) => void;
}

/** True once the server has rejected this bundle as too old. Never resets without a reload. */
export const useUpdateRequiredStore = create<UpdateRequiredState>((set) => ({
  required: false,
  minVersion: null,
  setRequired: (minVersion) => set({ required: true, minVersion }),
}));

interface UpdateRequiredShape {
  response?: { status?: number; data?: { error?: { code?: string }; minVersion?: unknown } };
}

/** True for a 426 response carrying the CLIENT_UPDATE_REQUIRED error code. */
export function isUpdateRequiredError(err: unknown): boolean {
  const res = (err as UpdateRequiredShape | null)?.response;
  return res?.status === 426 && res.data?.error?.code === ErrorCodes.CLIENT_UPDATE_REQUIRED;
}

/**
 * If `err` is a CLIENT_UPDATE_REQUIRED rejection, flip the app into the
 * blocking update state and return true.
 */
export function reportIfUpdateRequired(err: unknown): boolean {
  if (!isUpdateRequiredError(err)) return false;
  const min = (err as UpdateRequiredShape).response?.data?.minVersion;
  useUpdateRequiredStore.getState().setRequired(typeof min === 'string' ? min : null);
  return true;
}
